import React, {useEffect} from 'react';

import filter from 'lodash/filter';
import takeWhile from 'lodash/takeWhile';

import {SidebarItem} from 'app/general/structures/SidebarItem';
import {
  useSidebarIsOpenState,
  useToggleSidebarIsOpen,
} from 'app/settings/hooks';
import {
  type DashboardIsOpenPayloadType,
  VERSIONS_SIDEBAR_TOGGABLE,
} from 'app/settings/slice';
import {gt, ngt} from 'app/utils/i18n';

import type {
  DetailPolicySerializedType,
  ProcedureHashidSerializedType,
  ProcessHashidSerializedType,
  QuizSerializedType,
} from 'sweet-types';

type CommonVersionType = {
  approved_at: null | string;
  is_current: boolean;
};

function tooltipText(drafts: number) {
  return (
    <div>
      <div>{gt('Versions')}</div>
      {drafts > 0 && (
        <div>{ngt('%s pending draft', '%s pending drafts', drafts)}</div>
      )}
    </div>
  );
}

function getRecentVersions(versions: Array<CommonVersionType>) {
  return takeWhile(
    versions,
    (version) => !(version.is_current && version.approved_at)
  );
}

function getNumDrafts(recentVersions: Array<CommonVersionType>): number {
  return filter(recentVersions, {
    approved_at: null,
  }).length;
}

type Props<T extends CommonVersionType> = {
  object:
    | ProcessHashidSerializedType
    | ProcedureHashidSerializedType
    | DetailPolicySerializedType
    | QuizSerializedType;
  versions: Array<T>;
  link?:
    | string
    | {
        pathname: string;
        search?: string;
        hash?: string;
        state?: Record<string, any>;
      }
    | (() => string);
  active?: boolean;
  forceTooltipShow?: boolean;
};

export const VersionsSidebarItem = <T extends CommonVersionType>({
  object,
  versions,
  link,
  active,
  forceTooltipShow,
}: Props<T>) => {
  const toggleSidebarIsOpen = useToggleSidebarIsOpen();

  const sidebarProps: DashboardIsOpenPayloadType = {
    contentType: object.content_type,
    objectId: object.id,
    widgetName: VERSIONS_SIDEBAR_TOGGABLE,
  };
  const sidebarIsOpen = useSidebarIsOpenState(sidebarProps);
  const onToggle = () => {
    if (!sidebarIsOpen) {
      toggleSidebarIsOpen(sidebarProps);
    }
  };

  useEffect(() => {
    return () => {
      onToggle();
    };
  }, []);

  const recentVersions = getRecentVersions(versions);
  const numDrafts = getNumDrafts(recentVersions);
  return (
    <SidebarItem
      title={tooltipText(numDrafts)}
      icon={active && !sidebarIsOpen ? 'fa fa-chevron-right' : 'far fa-clock'}
      badge={numDrafts > 0 ? numDrafts : undefined}
      link={link}
      onClick={onToggle}
      active={active}
      forceTooltipShow={forceTooltipShow}
    />
  );
};
